import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Balance,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Chip,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Select,
  SkeletonRows,
  StatTile,
  Textarea,
  Toolbar,
  addDecimal,
  cn,
  formatBalance,
  formatMoney,
  isZeroDecimal,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { exportReport } from '../lib/export.js';
import { printReport, type ReportTable } from '../lib/print.js';
import { nextAccountCode } from '../lib/account-code.js';
import {
  ApiError,
  apiSend,
  useResource,
  type AccountWriteResult,
  type ApiAccount,
} from '../lib/api.js';
import { allCurrencies, baseCurrency } from '../lib/reference-data.js';

/**
 * The chart of accounts, from the database, and the screen that maintains it.
 *
 * Two things here are load-bearing and neither is obvious.
 *
 * **The indent is `depth`, not `depth - 1`.** `accounts.depth` is 0-based: a
 * top-level account is 0 and its children are 1. Subtracting one before
 * multiplying put levels 1 and 2 at the same indent, so the second level of the
 * tree rendered as a flat continuation of the first and the hierarchy only
 * became visible from level 3. Everything the user *says* — "second level",
 * "fifth level" — is `depth + 1`, which the API now returns as `level` so the
 * two numbers cannot drift apart.
 *
 * **A parent is barred from voucher entry, and the bar is the database's.**
 * Adding a sub-account flips the parent's `is_leaf` through
 * `trg_accounts_after_path`, and `trg_voucher_lines_derive` refuses a line on
 * anything that is not a leaf. So a 4th-level account that acquires 5th-level
 * detail stops accepting vouchers the moment the child is saved — this screen
 * only has to say so, and it does, on the row and in the detail panel.
 *
 * **Levels 1 to 4 are the chart; level 5 is the sub-ledger.** The first four
 * levels are the structure an accountant designs once — asset, current asset,
 * trade receivable, accounts receivable — and the fifth is the detail underneath
 * it that grows every week: the individual customer, supplier or salesperson.
 * They are not the same act, so the screen does not offer the same button for
 * both. `Add sub-account` appears on a 4th-level account and nowhere else; a
 * new branch of the structure itself goes through `New account`, which is where
 * a parent is chosen deliberately. `SUB_ACCOUNT_PARENT_LEVEL` is that rule.
 *
 * **The tree opens at three levels.** A five-level chart fully expanded is a
 * few thousand rows, and the first thing anybody did with it was collapse it.
 * `DEFAULT_OPEN_LEVELS` is applied once, when the accounts first arrive, and
 * `Levels 1–3` puts it back.
 */

const TYPES: Array<{ value: ApiAccount['type'] | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'ASSET', label: 'Assets' },
  { value: 'LIABILITY', label: 'Liabilities' },
  { value: 'EQUITY', label: 'Equity' },
  { value: 'INCOME', label: 'Income' },
  { value: 'EXPENSE', label: 'Expense' },
];

const CONTROL_KINDS = [
  'RECEIVABLE',
  'PAYABLE',
  'COMMISSION_RECEIVABLE',
  'COMMISSION_PAYABLE',
  'INVENTORY',
  'BANK',
  'CASH',
  'TAX',
  'ADVANCE',
];

const CASH_FLOW = ['OPERATING', 'INVESTING', 'FINANCING'];

/** The deepest level the chart holds. `accounts_depth_ck` allows 0–5. */
const MAX_LEVEL = 5;

/**
 * The only level that may be given a sub-account.
 *
 * A 5th-level account is the sub-ledger detail — one customer, one supplier —
 * and it hangs off the 4th-level control account that summarises it. Offering
 * `Add sub-account` on a 1st-, 2nd- or 3rd-level head would invite somebody to
 * hang a customer off `Assets`, and the account it silently stopped from taking
 * vouchers would be a whole branch of the balance sheet.
 */
const SUB_ACCOUNT_PARENT_LEVEL = 4;

/** How much of the tree is open when the screen loads. */
const DEFAULT_OPEN_LEVELS = 3;

const LEVEL_NAME = ['', 'Top level', '2nd level', '3rd level', '4th level', '5th level'];

interface Draft {
  mode: 'create' | 'edit';
  /** The row being edited; absent when creating. */
  original?: ApiAccount;
  /**
   * The code, which nobody chooses.
   *
   * On a create it is `nextAccountCode`'s preview of what the server will issue;
   * on an edit it is the account's own. Displayed, never posted. There used to be
   * a `codeTouched` flag beside it, guarding a value somebody had typed against
   * being re-derived — with the code positional, there is no such value.
   */
  code: string;
  /** Creating 5th-level detail under a 4th-level head: the parent is fixed. */
  subAccount: boolean;
  name: string;
  parentCode: string;
  type: ApiAccount['type'];
  normalBalance: 'DR' | 'CR';
  controlKind: string;
  allowDirectPosting: boolean;
  isContra: boolean;
  requiresParty: boolean;
  /** Which foreign currency this account holds, or '' for base only. */
  fcyCurrency: string;
  statementGroup: string;
  cashFlowClass: string;
  isActive: boolean;
  notes: string;
}

export function ChartOfAccountsScreen({
  onOpenLedger,
  onChartChanged,
}: {
  onOpenLedger: (accountCode: string) => void;
  /**
   * Tell the application the chart has moved.
   *
   * This screen reads `/accounts?include=inactive` and `App` reads `/accounts`
   * — two requests, two caches. Reloading only this one leaves the registry
   * behind `findAccount()` holding the chart as it was when the session
   * started, so a sub-account created here would not appear in the voucher
   * form's Sub account column until the page was refreshed. Since adding one
   * also stops its parent taking voucher lines, the stale copy would go on
   * offering an account the database has just started refusing.
   */
  onChartChanged: () => void;
}) {
  // `include=inactive` — this is the maintenance view, and an account somebody
  // switched off has to be visible here to be switched back on.
  const { data, error, loading, reload } = useResource<{ accounts: ApiAccount[] }>('/accounts', {
    include: 'inactive',
  });
  const accounts = useMemo(() => data?.accounts ?? [], [data]);

  const [query, setQuery] = useState('');
  const [type, setType] = useState<ApiAccount['type'] | 'ALL'>('ALL');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  /**
   * Off by default: the chart is the structure an accountant designed, and the
   * fifth level is the people the fourth summarises. They are opened and edited
   * in their own registers — Sales & Purchase for customers and suppliers — and
   * the chart's job is to show that the connection exists and what it adds up
   * to, not to be a party list. Search still finds them.
   */
  const [showSubLedger, setShowSubLedger] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * Heads whose every child is sub-ledger detail — `1131`, `2111`, `2121`.
   *
   * With the detail hidden their chevron would expand onto nothing, which reads
   * as a broken control rather than a hidden one. So they lose the chevron and
   * keep the badge, and the badge is the thing that says how many are down
   * there and where they are maintained.
   */
  const subLedgerOnlyHeads = useMemo(() => {
    const total = new Map<string, number>();
    const detail = new Map<string, number>();
    for (const a of accounts) {
      if (!a.parentCode) continue;
      total.set(a.parentCode, (total.get(a.parentCode) ?? 0) + 1);
      if (a.subLedgerKind) detail.set(a.parentCode, (detail.get(a.parentCode) ?? 0) + 1);
    }
    return new Set(
      [...detail.entries()].filter(([code, n]) => n === total.get(code)).map(([code]) => code),
    );
  }, [accounts]);

  const byCode = useMemo(() => new Map(accounts.map((a) => [a.code, a])), [accounts]);
  const current = selected ? byCode.get(selected) : undefined;

  /**
   * Everything from `DEFAULT_OPEN_LEVELS` down, shut.
   *
   * Collapsing a 3rd-level head hides its 4th level and, transitively, the 5th
   * underneath — `visible` already propagates a hidden parent — so shutting
   * level 3 and deeper is what leaves levels 1 to 3 on screen.
   */
  const defaultCollapsed = useMemo(
    () =>
      new Set(
        accounts
          .filter((a) => a.level >= DEFAULT_OPEN_LEVELS && a.childCount > 0)
          .map((a) => a.code),
      ),
    [accounts],
  );

  /**
   * Applied once, when the accounts land — not on every change to them.
   *
   * `reload()` after a save re-runs this effect with a new array, and without
   * the latch a newly created sub-account would re-collapse the branch the
   * accountant is working in every time they added a row to it.
   */
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || accounts.length === 0) return;
    opened.current = true;
    setCollapsed(defaultCollapsed);
  }, [accounts, defaultCollapsed]);

  /**
   * Search and the type filter both bypass the tree: when you are looking for
   * "6420" you want the row, not its four ancestors. With neither active the
   * hierarchy is honoured, including whatever is collapsed.
   */
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtering = needle !== '' || type !== 'ALL';

    const matches = accounts.filter((account) => {
      if (type !== 'ALL' && account.type !== type) return false;
      if (!needle) return true;
      return (
        account.code.toLowerCase().includes(needle) ||
        account.name.toLowerCase().includes(needle) ||
        (account.subLedgerName ?? '').toLowerCase().includes(needle)
      );
    });
    // Search bypasses the sub-ledger filter as well as the tree. Somebody
    // typing "Indus Corn" is looking for that account; answering "no match"
    // because a display preference is switched off would read as a missing
    // account rather than a hidden one.
    if (filtering) return matches;

    const hidden = new Set<string>();
    return accounts.filter((account) => {
      // Level-5 detail is maintained in its own register — parties in Sales &
      // Purchase, salespersons under Users — and there is one row per customer,
      // supplier and salesperson on the books. On a real book that is hundreds
      // of rows between `1131` and `1132`, which buries the chart an accountant
      // designed under the sub-ledger it summarises. The head still carries the
      // rolled-up balance, because the roll-up is by `path` and owes nothing to
      // what this draws.
      if (!showSubLedger && account.subLedgerKind) return false;

      const parentHidden = account.parentCode
        ? hidden.has(account.parentCode) || collapsed.has(account.parentCode)
        : false;
      if (parentHidden) {
        hidden.add(account.code);
        return false;
      }
      return true;
    });
  }, [accounts, query, type, collapsed, showSubLedger]);

  /**
   * The chart as it goes to paper and to Excel.
   *
   * Exactly what is on screen — `visible`, so a collapsed branch, a type filter
   * and a search are all honoured, because the printed copy of a filtered report
   * that quietly prints everything is the one somebody signs. The columns are
   * the table's own five.
   *
   * The indent is non-breaking spaces rather than a padding style: it has to
   * survive being a cell in a spreadsheet as well as a cell in a print, and the
   * hierarchy is most of what the chart is telling you.
   */
  const report = (): ReportTable => ({
    title: 'Chart of accounts',
    fileName: 'chart-of-accounts',
    meta: [
      `${visible.length} of ${accounts.length} accounts`,
      type === 'ALL' ? null : type.toLowerCase(),
      query.trim() ? `matching “${query.trim()}”` : null,
      showSubLedger ? null : 'sub-ledger detail hidden',
      `balances as at ${new Date().toLocaleDateString()}`,
    ]
      .filter(Boolean)
      .join(' · '),
    columns: [
      { label: 'Code', width: '88px' },
      { label: 'Level', width: '44px' },
      { label: 'Account' },
      { label: 'Nature', width: '108px' },
      { label: `Balance ${baseCurrency()}`, align: 'right', width: '124px' },
    ],
    rows: visible.map((account) => ({
      cells: [
        account.code,
        `L${account.level}`,
        `${'   '.repeat(account.depth)}${account.name}`,
        `${account.type.toLowerCase()} · ${account.normalBalance}`,
        isZeroDecimal(account.balanceBase) ? '—' : formatBalance(account.balanceBase),
      ],
      head: !account.isLeaf,
    })),
    notes: ['A group head rolls its descendants up and takes no voucher line of its own.'],
  });

  const stats = useMemo(() => {
    const leaves = accounts.filter((a) => a.isLeaf && a.isActive);
    const groups = accounts.filter((a) => !a.isLeaf);
    const deepest = accounts.reduce((max, a) => Math.max(max, a.level), 0);
    const debits = accounts
      .filter((a) => a.isLeaf && !isZeroDecimal(a.balanceBase))
      .reduce(
        (acc, a) => (a.balanceBase.startsWith('-') ? acc : addDecimal(acc, a.balanceBase)),
        '0',
      );
    return { leaves, groups, deepest, debits };
  }, [accounts]);

  const toggle = (code: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const openCreate = (parent?: ApiAccount, subAccount = false) =>
    setDraft({
      mode: 'create',
      code: nextAccountCode(parent, accounts),
      subAccount,
      name: '',
      parentCode: parent?.code ?? '',
      type: parent?.type ?? 'ASSET',
      normalBalance: parent?.normalBalance ?? 'DR',
      controlKind: '',
      allowDirectPosting: true,
      isContra: false,
      // 5th-level detail is the sub-ledger: it is *what* the party is, so it
      // does not also need one pointed at it.
      requiresParty: false,
      fcyCurrency: '',
      statementGroup: parent?.statementGroup ?? '',
      cashFlowClass: parent?.cashFlowClass ?? '',
      isActive: true,
      notes: '',
    });

  const openEdit = (account: ApiAccount) =>
    setDraft({
      mode: 'edit',
      original: account,
      code: account.code,
      subAccount: false,
      name: account.name,
      parentCode: account.parentCode ?? '',
      type: account.type,
      normalBalance: account.normalBalance,
      controlKind: account.controlKind ?? '',
      allowDirectPosting: account.allowDirectPosting,
      isContra: account.isContra,
      requiresParty: account.requiresParty,
      fcyCurrency: account.fcyCurrency ?? '',
      statementGroup: account.statementGroup ?? '',
      cashFlowClass: account.cashFlowClass ?? '',
      isActive: account.isActive,
      notes: account.notes ?? '',
    });

  /**
   * Returns the result rather than a boolean, because the caller needs the code.
   *
   * Since 0037 a create derives its code on the server and a move re-derives it,
   * so the row to select afterwards is the one the response names — the form only
   * ever held a preview of it.
   */
  const run = async <T extends { message: string }>(work: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setProblem(null);
    setNotice(null);
    try {
      const result = await work();
      setNotice(result.message);
      reload();
      onChartChanged();
      return result;
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.full : String(cause));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    const body: Record<string, unknown> = {
      name: draft.name.trim(),
      controlKind: draft.controlKind || null,
      allowDirectPosting: draft.allowDirectPosting,
      isContra: draft.isContra,
      requiresParty: draft.requiresParty,
      fcyCurrency: draft.fcyCurrency || null,
      statementGroup: draft.statementGroup.trim() || null,
      cashFlowClass: draft.cashFlowClass || null,
      notes: draft.notes.trim() || null,
    };

    if (draft.mode === 'create') {
      // No `code`. The server derives it from the parent and refuses one in the
      // body, so what the form showed was a preview — the code that comes back
      // in the response is the real one, and it is what gets selected below.
      Object.assign(body, {
        type: draft.type,
        normalBalance: draft.normalBalance,
        parentCode: draft.parentCode || null,
      });
      const created = await run(() =>
        apiSend<AccountWriteResult>('POST', '/accounts', body, auth()),
      );
      if (created) {
        setSelected(created.code);
        setDraft(null);
      }
      return;
    }

    body.isActive = draft.isActive;
    // Identity fields go only when they actually changed: sending them unchanged
    // would make an edit look like a re-code to an account that has postings.
    // `code` is never sent — it is derived from the parent since 0037, and the
    // server refuses it — so a re-code is spelled "change the parent".
    const before = draft.original;
    if (before && draft.type !== before.type) body.type = draft.type;
    if (before && draft.normalBalance !== before.normalBalance) {
      body.normalBalance = draft.normalBalance;
    }
    if (before && (draft.parentCode || null) !== before.parentCode) {
      body.parentCode = draft.parentCode || null;
    }

    const saved = await run(() =>
      apiSend<AccountWriteResult>(
        'PATCH',
        `/accounts/${encodeURIComponent(before?.code ?? '')}`,
        body,
        auth(),
      ),
    );
    if (saved) {
      // Moving an account re-derives its code, so the row to select afterwards is
      // the one the server named, not the one the form opened on.
      setSelected(saved.code);
      setDraft(null);
    }
  };

  const remove = async (account: ApiAccount) => {
    const ok = await run(() =>
      apiSend<{ message: string }>(
        'DELETE',
        `/accounts/${encodeURIComponent(account.code)}`,
        undefined,
        auth(),
      ),
    );
    if (ok) {
      setSelected(null);
      setDraft(null);
    }
  };

  return (
    <>
      <PageHeader
        breadcrumb="Finance & Accounts / Chart of Accounts"
        title="Chart of accounts"
        description="Every account the business keeps, with its current balance."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => printReport(report())}>
              Print / PDF
            </Button>
            <Button variant="secondary" size="sm" onClick={() => exportReport(report())}>
              Export XLSX
            </Button>
            {/* A 5th-level row has nothing that can sit under it, so selecting
                one falls back to creating a fresh top-level account rather than
                offering a parent the API would refuse. */}
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                openCreate(
                  current && current.level < MAX_LEVEL ? current : undefined,
                  current?.level === SUB_ACCOUNT_PARENT_LEVEL,
                )
              }
            >
              {current && current.level < MAX_LEVEL ? `New under ${current.code}` : 'New account'}
            </Button>
          </>
        }
        meta={
          <>
            <Chip>{accounts.length} accounts</Chip>
            <Chip>{stats.leaves.length} postable</Chip>
            <Chip>{stats.groups.length} group heads</Chip>
            <Chip>{stats.deepest} levels deep</Chip>
          </>
        }
      />

      {(error || problem || notice) && (
        <div className="flex flex-col gap-2.5 pb-3.5" data-noprint="1">
          {error && (
            <Callout tone="danger" title="Could not load the chart of accounts">
              {error}
            </Callout>
          )}
          {problem && (
            <Callout tone="danger" title="The database refused that change">
              {problem}
            </Callout>
          )}
          {notice && (
            <Callout tone="success" title="Saved">
              {notice}
            </Callout>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3.5 pb-3.5 lg:grid-cols-4">
        <Card>
          <StatTile
            label="Accounts"
            value={String(accounts.length)}
            hint={`${stats.leaves.length} take voucher lines`}
          />
        </Card>
        <Card>
          <StatTile
            label="Group heads"
            value={String(stats.groups.length)}
            hint="roll up, never posted to"
          />
        </Card>
        <Card>
          <StatTile label="Depth" value={`${stats.deepest} of ${MAX_LEVEL}`} hint="levels in use" />
        </Card>
        <Card>
          <StatTile
            label="Debit side"
            value={formatMoney(stats.debits, { decimals: 0 })}
            hint="leaf balances, PKR"
          />
        </Card>
      </div>

      <div className="flex flex-wrap items-start gap-3.5">
        <Card flush className="min-w-0 flex-[3_1_520px]">
          <Toolbar>
            <Input
              size="sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code or account name"
              aria-label="Search accounts"
              className="min-w-[200px] flex-1"
            />
            <div className="flex flex-wrap gap-1.5">
              {TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setType(option.value)}
                  aria-pressed={type === option.value}
                  className={cn(
                    'h-7 cursor-pointer rounded-[var(--radius-pill)] border px-2.5 text-[12px] font-medium',
                    'transition-colors duration-[var(--dur-fast)]',
                    type === option.value
                      ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                      : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-3)] hover:text-[var(--ink)]',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setCollapsed(new Set())}>
              Expand all
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCollapsed(defaultCollapsed)}>
              Levels 1–{DEFAULT_OPEN_LEVELS}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setCollapsed(new Set(accounts.filter((a) => !a.isLeaf).map((a) => a.code)))
              }
            >
              Collapse
            </Button>
            <Button
              size="sm"
              variant={showSubLedger ? 'secondary' : 'ghost'}
              aria-pressed={showSubLedger}
              onClick={() => setShowSubLedger((v) => !v)}
            >
              {showSubLedger ? 'Hide sub-ledger detail' : 'Show sub-ledger detail'}
            </Button>
          </Toolbar>

          {loading ? (
            <SkeletonRows rows={12} cols={4} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" aria-label="Chart of accounts">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th width="104px">Code</Th>
                    <Th width="58px">Level</Th>
                    <Th>Account</Th>
                    <Th width="132px">Nature</Th>
                    <Th width="164px" align="right">
                      Balance PKR
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((account) => {
                    const isCollapsed = collapsed.has(account.code);
                    const group = !account.isLeaf;

                    return (
                      <tr
                        key={account.code}
                        onClick={() => setSelected(account.code)}
                        className={cn(
                          'bb-row-h cursor-pointer border-b border-[var(--line-2)] transition-colors',
                          selected === account.code
                            ? 'bg-[var(--accent-soft)]'
                            : 'hover:bg-[var(--surface-2)]',
                          !account.isActive && 'opacity-60',
                        )}
                      >
                        {/*
                          `whitespace-nowrap`, because a code is dashed segments
                          since 0037 and the browser breaks on a hyphen: `1-1-1`
                          wrapped to two lines in this column and read as two
                          different codes. The hierarchy is the one thing the code
                          is *for*, so it has to arrive in one piece.
                        */}
                        <td className="bb-mono whitespace-nowrap px-3.5 text-[12.5px] text-[var(--ink-2)]">
                          {account.code}
                        </td>
                        <td className="bb-mono px-2 text-[11.5px] text-[var(--ink-3)]">
                          L{account.level}
                        </td>
                        <td className="px-2">
                          <span
                            className="flex items-center gap-2"
                            /* depth, not depth − 1: `depth` is 0-based, so the
                               old expression collapsed levels 1 and 2 onto the
                               same indent and hid the second level of the tree. */
                            style={{ paddingLeft: `${account.depth * 16}px` }}
                          >
                            {account.childCount > 0 &&
                            (showSubLedger || !subLedgerOnlyHeads.has(account.code)) ? (
                              <button
                                type="button"
                                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggle(account.code);
                                }}
                                className="w-4 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-none text-[var(--ink-3)] hover:text-[var(--ink)]"
                              >
                                {isCollapsed ? '▸' : '▾'}
                              </button>
                            ) : (
                              <span className="w-4 shrink-0" aria-hidden="true" />
                            )}
                            <span
                              className={cn(
                                'whitespace-nowrap',
                                group
                                  ? 'text-[13px] font-semibold text-[var(--ink)]'
                                  : 'text-[12.5px] text-[var(--ink-2)]',
                              )}
                            >
                              {account.name}
                            </span>
                            {group && (
                              <Badge tone="mute" className="shrink-0">
                                {!showSubLedger && subLedgerOnlyHeads.has(account.code)
                                  ? `${account.childCount} sub-ledger · no entry`
                                  : `${account.childCount} sub · no entry`}
                              </Badge>
                            )}
                            {account.isControl && (
                              <Badge tone="info" className="shrink-0">
                                control
                              </Badge>
                            )}
                            {account.subLedgerKind && (
                              <Badge tone="mute" className="shrink-0">
                                {account.subLedgerKind === 'salesperson' ? 'salesperson' : 'party'}
                              </Badge>
                            )}
                            {account.fcyCurrency && (
                              <Badge tone="warn" className="shrink-0">
                                {account.fcyCurrency}
                              </Badge>
                            )}
                            {account.isContra && (
                              <Badge tone="mute" className="shrink-0">
                                contra
                              </Badge>
                            )}
                            {!account.isActive && (
                              <Badge tone="bad" className="shrink-0">
                                inactive
                              </Badge>
                            )}
                          </span>
                        </td>
                        <td className="px-2 text-[12px] whitespace-nowrap text-[var(--ink-3)]">
                          {account.type.toLowerCase()} · {account.normalBalance}
                        </td>
                        <td className="px-4 text-right">
                          {isZeroDecimal(account.balanceBase) ? (
                            <span className="bb-num text-[12.5px] text-[var(--ink-3)]">—</span>
                          ) : (
                            <Balance value={account.balanceBase} className="text-[12.5px]" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t border-[var(--line)] px-3.5 py-2.5 text-[11.5px] text-[var(--ink-3)]">
            {visible.length} of {accounts.length} shown · a code is frozen once a voucher posts to
            the account
          </div>
        </Card>

        <Card flush data-noprint="1" className="min-w-0 max-w-[380px] flex-[1_1_340px]">
          {draft ? (
            <AccountForm
              draft={draft}
              accounts={accounts}
              busy={busy}
              onChange={setDraft}
              onCancel={() => setDraft(null)}
              onSave={save}
            />
          ) : current ? (
            <AccountDetail
              account={current}
              busy={busy}
              onOpenLedger={onOpenLedger}
              onEdit={() => openEdit(current)}
              onAddChild={() => openCreate(current, true)}
              onDelete={() => remove(current)}
            />
          ) : (
            <>
              <CardHeader title="Account detail" subtitle="Select a row" />
              <CardBody>
                <p className="m-0 text-[12.5px] text-[var(--ink-2)]">
                  Every flag here drives real behaviour: a control account refuses a manual journal
                  voucher, a foreign-currency account refuses a line in a third currency, and a
                  group head refuses a posting outright — which is what happens to a 4th-level
                  account the moment it is given a 5th-level sub-account.
                </p>
              </CardBody>
            </>
          )}
        </Card>
      </div>
    </>
  );
}

// ── Detail ───────────────────────────────────────────────────────────────────

function AccountDetail({
  account,
  busy,
  onOpenLedger,
  onEdit,
  onAddChild,
  onDelete,
}: {
  account: ApiAccount;
  busy: boolean;
  onOpenLedger: (code: string) => void;
  onEdit: () => void;
  onAddChild: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setConfirming(false), [account.code]);

  const canHoldSubAccounts = account.level === SUB_ACCOUNT_PARENT_LEVEL;
  const postable = account.isLeaf && account.isActive;
  const barredBecause = !account.isLeaf
    ? `${account.childCount} sub-account${account.childCount === 1 ? '' : 's'} sit under it, so voucher lines belong on those`
    : !account.isActive
      ? 'it has been deactivated'
      : null;

  return (
    <>
      <CardHeader
        eyebrow={account.code}
        title={account.name}
        subtitle={LEVEL_NAME[account.level] ?? `Level ${account.level}`}
        actions={<Badge tone={postable ? 'ok' : 'mute'}>{postable ? 'postable' : 'group'}</Badge>}
      />
      <CardBody className="flex flex-col gap-4">
        {barredBecause && (
          <Callout tone="info" title="Not available for voucher entry">
            {barredBecause}. The rule is the database&rsquo;s —{' '}
            <code className="bb-mono">trg_voucher_lines_derive</code> refuses a line on anything
            that is not a leaf.
          </Callout>
        )}

        {canHoldSubAccounts && account.childCount === 0 && !account.hasPostings && (
          <Callout tone="warn" title="Adding a sub-account closes this account to vouchers">
            {account.code} takes voucher lines today. Give it 5th-level detail and it becomes a
            group head: every voucher must then name one of the sub-accounts instead, and the picker
            on JV, CP, CR, BP and BR will ask for it.
          </Callout>
        )}

        {account.subLedgerKind && (
          <Callout tone="info" title="This is one person's sub-ledger">
            {account.subLedgerCode} {account.subLedgerName} — a{' '}
            {account.subLedgerKind === 'salesperson' ? 'salesperson' : 'party'} the company owes.
            The 4th-level account above rolls this and every other one up, which is what makes it
            reconcile; the ledger here is what is owed to this one.
          </Callout>
        )}

        <KeyValue
          items={[
            { label: 'Level', value: `${account.level} of ${MAX_LEVEL}` },
            { label: 'Parent', value: account.parentCode ?? '— top level', mono: true },
            ...(account.subLedgerKind
              ? [
                  {
                    label: account.subLedgerKind === 'salesperson' ? 'Salesperson' : 'Party',
                    value: `${account.subLedgerCode} · ${account.subLedgerName}`,
                  },
                ]
              : []),
            { label: 'Sub-accounts', value: String(account.childCount) },
            { label: 'Type', value: account.type.toLowerCase() },
            {
              label: 'Normal balance',
              value: account.normalBalance === 'DR' ? 'Debit' : 'Credit',
            },
            {
              label: 'Control',
              value: account.isControl ? (account.controlKind ?? 'yes') : 'no',
            },
            {
              label: 'Manual JV',
              value: account.allowDirectPosting ? 'allowed' : 'through its document only',
            },
            { label: 'Currency', value: account.fcyCurrency ?? 'PKR only' },
            { label: 'Party required', value: account.requiresParty ? 'yes' : 'no' },
            { label: 'Statement group', value: account.statementGroup ?? '—' },
            { label: 'Cash flow', value: account.cashFlowClass ?? '—' },
            { label: 'Movements', value: String(account.movements) },
            {
              label: 'Balance',
              value: <Balance value={account.balanceBase} />,
              mono: true,
              tone: 'strong' as const,
            },
          ]}
        />

        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            fullWidth
            disabled={!postable}
            onClick={() => onOpenLedger(account.code)}
            title={postable ? undefined : 'A group head has no ledger of its own'}
          >
            Open ledger
          </Button>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1" onClick={onEdit} disabled={busy}>
              Edit
            </Button>
            {/*
              Sub-accounts hang off the 4th level and nowhere else. On any other
              row the button is not disabled-with-a-tooltip but absent: a greyed
              control invites somebody to hunt for the permission that would
              enable it, and there is none — the level is the whole answer.
            */}
            {canHoldSubAccounts && (
              <Button
                size="sm"
                className="flex-1"
                onClick={onAddChild}
                disabled={busy}
                title={`Add a level ${SUB_ACCOUNT_PARENT_LEVEL + 1} sub-account under ${account.code}`}
              >
                Add sub-account
              </Button>
            )}
          </div>

          {confirming ? (
            <Callout tone="warn" title={`Delete ${account.code}?`}>
              <p className="m-0 pb-2 text-[12px]">
                This removes the account outright. It is refused if anything has ever posted to it.
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="danger" onClick={onDelete} loading={busy}>
                  Delete
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                  Keep it
                </Button>
              </div>
            </Callout>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              fullWidth
              onClick={() => setConfirming(true)}
              disabled={busy || account.isSystem || account.hasPostings || account.childCount > 0}
              title={
                account.isSystem
                  ? 'A system account is resolved by key — deleting it breaks the routine that uses it'
                  : account.hasPostings
                    ? 'This account has ledger lines. Deactivate it instead.'
                    : account.childCount > 0
                      ? 'Delete or re-parent its sub-accounts first'
                      : undefined
              }
            >
              Delete account
            </Button>
          )}
        </div>
      </CardBody>
    </>
  );
}

// ── Form ─────────────────────────────────────────────────────────────────────

function AccountForm({
  draft,
  accounts,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  draft: Draft;
  accounts: ApiAccount[];
  busy: boolean;
  onChange: (draft: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    onChange({ ...draft, [key]: value });

  /**
   * Who can be a parent.
   *
   * Anything already at the deepest level is out, and so is the account being
   * edited and everything beneath it — re-parenting a branch under itself is the
   * cycle `trg_accounts_path` raises on.
   */
  const parents = useMemo(() => {
    const self = draft.original?.code;
    const descendants = new Set<string>();
    if (self) {
      descendants.add(self);
      for (const a of accounts) {
        if (a.parentCode && descendants.has(a.parentCode)) descendants.add(a.code);
      }
    }
    return accounts.filter((a) => a.level < MAX_LEVEL && !descendants.has(a.code));
  }, [accounts, draft.original]);

  /**
   * Re-point the parent, and take the code with it.
   *
   * The code *is* the parent's code with a segment added, so moving the parent
   * moves the code — there is nothing to protect here any more, because nobody
   * types one. On an edit the preview is left alone: the server re-derives it on
   * save and the panel reads back what it decided.
   */
  const chooseParent = (parentCode: string) => {
    const next = accounts.find((a) => a.code === parentCode);
    onChange({
      ...draft,
      parentCode,
      code: draft.mode === 'create' ? nextAccountCode(next, accounts) : draft.code,
      // A branch of the tree rolls into one statement line, so a child
      // takes its parent's type; the API refuses a mismatch anyway.
      type: next?.type ?? draft.type,
      normalBalance:
        draft.mode === 'create'
          ? (next?.normalBalance ?? draft.normalBalance)
          : draft.normalBalance,
      statementGroup: draft.mode === 'create' ? (next?.statementGroup ?? '') : draft.statementGroup,
    });
  };

  const parent = draft.parentCode ? accounts.find((a) => a.code === draft.parentCode) : undefined;
  const level = parent ? parent.level + 1 : 1;
  const frozen = draft.mode === 'edit' && Boolean(draft.original?.hasPostings);
  const parentHasPostings = Boolean(parent?.hasPostings);

  return (
    <>
      <CardHeader
        title={
          draft.mode === 'edit'
            ? `Edit ${draft.original?.code}`
            : draft.subAccount
              ? `New sub-account under ${draft.parentCode}`
              : 'New account'
        }
        subtitle={
          parent
            ? `${LEVEL_NAME[level] ?? `Level ${level}`} under ${parent.code} ${parent.name}`
            : 'Top level'
        }
      />
      <CardBody className="flex flex-col gap-3.5">
        {frozen && (
          <Callout tone="warn" title="This account has postings">
            Its code, type and normal balance are frozen and so is its place in the tree — the name
            and the flags are still yours to change.
          </Callout>
        )}
        {draft.mode === 'create' && parentHasPostings && (
          <Callout tone="danger" title={`${parent?.code} already has vouchers`}>
            Adding a sub-account would make it a group head while it still owns the lines posted to
            it. The database refuses this; pick a parent with no postings.
          </Callout>
        )}
        {draft.mode === 'create' && parent && !parentHasPostings && (
          <Callout tone="info" title={`${parent.code} stops taking voucher lines`}>
            Once this saves, {parent.code} becomes a group head. Every voucher that named it must
            name a {LEVEL_NAME[level]?.toLowerCase() ?? `level ${level}`} account instead.
          </Callout>
        )}

        <Field
          label="Parent"
          hint={
            draft.subAccount
              ? 'A sub-account belongs to the 4th-level account it details, so this one is fixed.'
              : `A new account here sits at level ${level} of ${MAX_LEVEL}.`
          }
        >
          <Select
            size="sm"
            value={draft.parentCode}
            // In sub-account mode the parent is the row the button was pressed
            // on. Leaving it editable would be a second, quieter way to hang
            // 5th-level detail off a 2nd-level head.
            disabled={frozen || draft.subAccount}
            onChange={(e) => chooseParent(e.target.value)}
          >
            <option value="">— top level —</option>
            {parents.map((a) => (
              <option key={a.code} value={a.code}>
                {' '.repeat((a.level - 1) * 2)}
                {a.code} · {a.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-2.5">
          {/*
            Read-only, and not merely discouraged. The code is one segment per
            level — `1-1-2` is the second child of the second child of Assets — so
            it says where the account sits, and a box somebody could type in would
            be a second statement about that which could contradict the first.
            `POST /api/accounts` refuses a code in the body for the same reason,
            and derives it with `next_account_code`. What is shown here is that
            derivation run against the chart the browser last fetched, so it can
            be one behind somebody else's insert — which is why the panel selects
            the code the response comes back with rather than this one.
          */}
          <Field
            label="Code"
            hint={
              draft.mode === 'create'
                ? 'Issued from the parent when this is saved.'
                : 'Follows the parent. Move the account to change it.'
            }
          >
            <Input size="sm" value={draft.code || '—'} readOnly className="bb-mono" />
          </Field>
          <Field label="Normal balance" required>
            <Select
              size="sm"
              value={draft.normalBalance}
              disabled={frozen}
              onChange={(e) => set('normalBalance', e.target.value as 'DR' | 'CR')}
            >
              <option value="DR">Debit</option>
              <option value="CR">Credit</option>
            </Select>
          </Field>
        </div>

        <Field label="Name" required>
          <Input
            size="sm"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Accounts Receivable — Poultry"
          />
        </Field>

        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Type" required>
            <Select
              size="sm"
              value={draft.type}
              disabled={frozen || Boolean(parent)}
              onChange={(e) => set('type', e.target.value as ApiAccount['type'])}
            >
              {TYPES.filter((t) => t.value !== 'ALL').map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Cash flow">
            <Select
              size="sm"
              value={draft.cashFlowClass}
              onChange={(e) => set('cashFlowClass', e.target.value)}
            >
              <option value="">—</option>
              {CASH_FLOW.map((c) => (
                <option key={c} value={c}>
                  {c.toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Control account">
            <Select
              size="sm"
              value={draft.controlKind}
              onChange={(e) =>
                onChange({
                  ...draft,
                  controlKind: e.target.value,
                  // Cash and bank stay open to a manual JV; every other control
                  // account is reached through its sub-ledger document.
                  allowDirectPosting:
                    e.target.value === '' || e.target.value === 'CASH' || e.target.value === 'BANK',
                })
              }
            >
              <option value="">not a control account</option>
              {CONTROL_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.toLowerCase().replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Statement group">
            <Input
              size="sm"
              value={draft.statementGroup}
              onChange={(e) => set('statementGroup', e.target.value)}
              placeholder="RECEIVABLES"
            />
          </Field>
        </div>

        <div className="flex flex-col gap-2">
          <Checkbox
            checked={draft.allowDirectPosting}
            onChange={(e) => set('allowDirectPosting', e.target.checked)}
            label="Allow a manual journal voucher"
            description="Off means the account is reached only through its own document."
          />
          <Checkbox
            checked={draft.requiresParty}
            onChange={(e) => set('requiresParty', e.target.checked)}
            label="Every line needs a party"
            description="A receivable or payable that cannot be reconciled without one."
          />
          <Checkbox
            checked={draft.isContra}
            onChange={(e) => set('isContra', e.target.checked)}
            label="Contra account"
            description="Normal balance opposite to its type — sales returns, accumulated depreciation."
          />
          {/* A picker, not a checkbox: the checkbox meant USD, so an account
              holding EUR could not be described at all once Setup could add
              EUR. `trg_voucher_lines_derive` refuses a line in any currency
              other than this one or base, which is why the field names the
              currency rather than merely admitting to having one. */}
          <Field
            label="Foreign currency held"
            hint={`Lines in any currency other than this one or ${baseCurrency()} are refused`}
          >
            <Select value={draft.fcyCurrency} onChange={(e) => set('fcyCurrency', e.target.value)}>
              <option value="">{baseCurrency()} only</option>
              {allCurrencies()
                .filter((c) => !c.isBase)
                .map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
            </Select>
          </Field>
          {draft.mode === 'edit' && (
            <Checkbox
              checked={draft.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
              label="Active"
              description="Deactivating keeps the history and stops any new voucher naming it."
            />
          )}
        </div>

        <Field label="Notes">
          <Textarea
            rows={2}
            value={draft.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Why this account exists, what belongs in it."
          />
        </Field>

        {/*
          An empty code preview means the parent is already at the deepest level,
          so there is nothing for the server to issue and nothing to save.
        */}
        <div className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1"
            loading={busy}
            disabled={draft.code.trim() === '' || draft.name.trim() === ''}
            onClick={onSave}
          >
            {draft.mode === 'create' ? 'Create account' : 'Save changes'}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        </div>
      </CardBody>
    </>
  );
}

function Th({
  children,
  width,
  align = 'left',
}: {
  children: React.ReactNode;
  width?: string;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      className={cn(
        'border-b border-[var(--line-2)] px-3.5 py-2',
        'text-[10px] font-medium tracking-[0.08em] whitespace-nowrap text-[var(--ink-3)] uppercase',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}
